import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, User, Cake } from "lucide-react";
import { CATEGORIES, OFFERS } from "../mock";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const Header = () => {
  const { count, setIsOpen } = useCart();
  const { user, logout, openLogin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/collections/all?q=${encodeURIComponent(query)}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Offer marquee */}
      <div className="bg-[#6B4226] text-[#FFF8F0] text-xs md:text-sm overflow-hidden py-2">
        <div className="marquee-track whitespace-nowrap">
          {[...OFFERS, ...OFFERS].map((o, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-2">
              <Cake size={14} className="text-[#E8A6B8]" /> {o}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-[#FFF8F0]/95 backdrop-blur border-b border-[#EADBCB]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-[70px] gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img src="/assets/globaltreats-logo.jpeg" alt="Globaltreats logo" className="w-11 h-11 rounded-full object-cover ring-1 ring-[#EADBCB]" />
              <div className="leading-none">
                <div className="font-display text-xl md:text-2xl font-bold text-[#6B4226]">Globaltreats</div>
                <div className="font-script text-sm md:text-base text-[#D46A8A] normal-case tracking-normal leading-none">Cake Creation by Swati</div>
              </div>
            </Link>

            {/* Search - desktop */}
            <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md mx-4">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B07A5C]" size={18} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for cakes, flavours..."
                  className="w-full pl-10 pr-4 h-11 rounded-full bg-white border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A] transition-colors"
                />
              </div>
            </form>

            {/* Actions */}
            <div className="flex items-center gap-1 md:gap-2">
              {user ? (
                <div className="hidden md:flex items-center gap-2">
                  <Link to="/my-orders" className="flex items-center gap-2 group" data-testid="header-my-orders-link">
                    <div className="w-9 h-9 rounded-full bg-[#E8A6B8] text-white grid place-items-center text-sm font-semibold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <span className="text-sm text-[#6B4226] group-hover:text-[#D46A8A]">My Orders</span>
                  </Link>
                  <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-[#6B4226] hover:text-[#D46A8A]" data-testid="header-logout-btn">Logout</button>
                </div>
              ) : (
                <button onClick={() => openLogin("/my-orders")} className="hidden md:flex items-center gap-1.5 text-sm text-[#6B4226] hover:text-[#D46A8A] px-3 py-2 rounded-full" data-testid="header-login-btn">
                  <User size={18} /> Login
                </button>
              )}
              <Link to="/blog" className="hidden md:flex items-center gap-1.5 text-sm text-[#6B4226] hover:text-[#D46A8A] px-3 py-2 rounded-full">
                Journal
              </Link>
              <Link to="/track-order" className="hidden md:flex items-center gap-1.5 text-sm text-[#6B4226] hover:text-[#D46A8A] px-3 py-2 rounded-full">
                Track Order
              </Link>
              <button
                onClick={() => setIsOpen(true)}
                className="relative w-11 h-11 grid place-items-center rounded-full hover:bg-[#F6E7DA] transition-colors"
                aria-label="Cart"
                data-testid="header-cart-btn"
              >
                <ShoppingBag size={22} className="text-[#6B4226]" />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#D46A8A] text-white text-[11px] min-w-[20px] h-5 px-1 rounded-full grid place-items-center">
                    {count}
                  </span>
                )}
              </button>
              <button className="md:hidden w-11 h-11 grid place-items-center" onClick={() => setMobileOpen((o) => !o)}>
                {mobileOpen ? <X size={24} className="text-[#6B4226]" /> : <Menu size={24} className="text-[#6B4226]" />}
              </button>
            </div>
          </div>

          {/* Category nav - desktop */}
          <nav className="hidden md:flex items-center gap-1 h-12 overflow-x-auto">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/collections/${c.slug}`}
                className="whitespace-nowrap text-sm font-medium text-[#6B4226] px-3 py-1.5 rounded-full hover:bg-[#F6E7DA] hover:text-[#D46A8A] transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </nav>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-[#EADBCB] bg-[#FFF8F0] px-4 py-4 space-y-3">
            <form onSubmit={submitSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B07A5C]" size={18} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search cakes..."
                  className="w-full pl-10 pr-4 h-11 rounded-full bg-white border border-[#EADBCB] text-sm outline-none"
                />
              </div>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map((c) => (
                <Link key={c.slug} to={`/collections/${c.slug}`} onClick={() => setMobileOpen(false)}
                  className="text-sm font-medium text-[#6B4226] bg-white border border-[#EADBCB] rounded-xl px-3 py-2">
                  {c.name}
                </Link>
              ))}
            </div>
            <Link to="/blog" onClick={() => setMobileOpen(false)} className="block text-center w-full h-11 leading-[44px] rounded-full bg-white border border-[#EADBCB] text-[#6B4226] text-sm">Journal &amp; Cake Ideas</Link>
            {user ? (
              <div className="space-y-2">
                <Link to="/my-orders" onClick={() => setMobileOpen(false)} className="block text-center w-full h-11 leading-[44px] rounded-full bg-[#E8A6B8] text-white text-sm" data-testid="mobile-my-orders-link">My Orders</Link>
                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full h-11 rounded-full bg-[#6B4226] text-white text-sm">Logout</button>
              </div>
            ) : (
              <button onClick={() => { openLogin("/my-orders"); setMobileOpen(false); }} className="block text-center w-full h-11 leading-[44px] rounded-full bg-[#6B4226] text-white text-sm" data-testid="mobile-login-btn">Login with WhatsApp</button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
